import {
  decapsulateSecurePayload,
  decryptPrivateKey,
  encapsulateSecurePayload,
} from "@/lib/crypto/crypto";

import { getUserData } from "@/server/services/userService";
import {
  GroupChatMember,
  MemberFromPrefixSearch,
  Member,
  MemberRole,
} from "@/server/types/member";
import { useNewGroupChatState } from "@/zustand/NewGroupChatState";
import { useNavigationState } from "@/zustand/NavigationState";
import { navigateToChatList } from "@/lib/navigationRef";
import { isUserAuthenticated } from "./auth";
import {
  addMembers,
  deleteMember,
  getMember,
  getMembers,
  patchMember,
  patchMemberOwnership,
  searchMembers,
} from "@/server/services/memberService";
import {
  removeChatFromLocalDB,
  // updateChatInLocalDB,
} from "@/indexedDB/services/chats";
import { removeChatDataFromLocalDB } from "@/indexedDB/services/chatTeardown";
import { useUserAvatarMapState } from "@/zustand/UserAvatarMapState";
import { rotateCSK } from "./chats";
import { fetchPinnedEpochs } from "@/server/services/epochService";
import { resolveCSK } from "./epochKeyCache";

/**
 * Seeds the UserAvatarMapState with avatar data from member API responses.
 * Uses the raw attachment IDs before they're processed into CDN URLs.
 */
const seedAvatarMapFromMembers = (
  members: Array<{ username: string; profile_image: string }>,
) => {
  const now = new Date().toISOString();
  const store = useUserAvatarMapState.getState();

  const entries: Array<{
    username: string;
    attachmentId: string;
    timestamp: string;
  }> = [];
  for (const m of members) {
    if (!m.username) {
      continue;
    }
    if (m.profile_image) {
      entries.push({
        username: m.username,
        attachmentId: m.profile_image,
        timestamp: now,
      });
    } else {
      // Tombstone: avatar was deleted while we were offline
      store.removeUserAvatar(m.username);
    }
  }
  if (entries.length > 0) {
    store.setUserAvatarBatch(entries);
  }
};

export const prepareMemberForExistingGroupChat = async (
  username: string,
  chatId: string,
  eCSK: string,
  signal?: AbortSignal,
): Promise<GroupChatMember | null> => {
  if (signal?.aborted) {
    return null;
  }
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  if (!username) {
    throw new Error("Member username not provided");
  }

  if (!eCSK) {
    throw new Error("eCSK not provided.");
  }

  if (!chatId) {
    throw new Error("Chat ID not provided.");
  }

  try {
    const member = await getUserData(username, signal);

    if (!member) {
      throw new Error("Member not found");
    }

    // Get current user's private key
    const senderPrivateKeys = await decryptPrivateKey(
      userFingerPrint.ePrK,
      userFingerPrint.dk,
    );

    // decapsulate chat secret key
    const csk = decapsulateSecurePayload({
      privateKeys: senderPrivateKeys,
      securePayload: eCSK,
    });

    // Generate secure payload for member
    const memberSecurePayload = encapsulateSecurePayload({
      receiverPublicKeys: member.public_keys,
      senderPrivateKeys,
      chatSecretKey: csk,
    });

    // Encapsulate pinned-epoch CSKs for the new member (spec §11.10)
    // so they can decrypt pinned messages from older epochs.
    let pinnedEpochPayloads: Record<number, string> | undefined;
    try {
      const pinnedEpochs = await fetchPinnedEpochs(chatId);
      if (pinnedEpochs.length > 0) {
        pinnedEpochPayloads = {};
        for (const epoch of pinnedEpochs) {
          // Resolve the CSK for each pinned epoch (from our own cache/server)
          const epochCSK = await resolveCSK(chatId, epoch, eCSK);
          // Encapsulate that CSK for the new member
          pinnedEpochPayloads[epoch] = encapsulateSecurePayload({
            receiverPublicKeys: member.public_keys,
            senderPrivateKeys,
            chatSecretKey: epochCSK,
          });
        }
      }
    } catch {
      // Non-fatal: new member just won't be able to read pinned messages from old epochs
      console.warn(
        `Failed to prepare pinned epoch payloads for ${username} in chat ${chatId}`,
      );
    }

    return {
      username,
      chat_id: chatId,
      role: MemberRole.MEMBER,
      secure_payload: memberSecurePayload,
      profile_image: member.profile_image,
      pinned_epoch_payloads: pinnedEpochPayloads,
    };
  } catch (error) {
    // Handle error
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Unable to prepare member.");
  }
};

export const prepareMemberForNewGroupChat = async (
  username: string,
  signal?: AbortSignal,
): Promise<void> => {
  if (signal?.aborted) {
    return;
  }
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  if (!username) {
    throw new Error("Member username not provided");
  }

  const csk = useNewGroupChatState.getState().newCSK;
  const chatId = useNewGroupChatState.getState().newChatId;

  if (!csk) {
    throw new Error("CSK not provided.");
  }

  if (!chatId) {
    throw new Error("Chat ID not provided.");
  }

  // prevent double member processing for the owner member
  // TO DO => Check with BE about the owner showing in the results
  // when search is being performed by the same member => owner
  if (userFingerPrint.username === username) {
    return;
  }

  // const usernames = useNewGroupChatState.getState().usernames;
  const addUsername = useNewGroupChatState.getState().addUsername;
  const removeUsername = useNewGroupChatState.getState().removeUsername;

  // const members = useNewGroupChatState.getState().members;
  const addChatMember = useNewGroupChatState.getState().addChatMember;
  const removeChatMember = useNewGroupChatState.getState().removeChatMember;

  try {
    // optimistically add to usernames in NewGroupChatState => to update check state
    addUsername(username);

    // // TO DO = > optimistically add to members collection in local db

    const member = await getUserData(username, signal);

    if (!member) {
      throw new Error("Member not found");
    }

    // Get current user's private key
    const senderPrivateKeys = await decryptPrivateKey(
      userFingerPrint.ePrK,
      userFingerPrint.dk,
    );

    // Generate secure payload for member
    const memberSecurePayload = encapsulateSecurePayload({
      receiverPublicKeys: member.public_keys,
      senderPrivateKeys,
      chatSecretKey: csk,
    });

    const newMember: GroupChatMember = {
      username,
      chat_id: chatId,
      role: MemberRole.MEMBER,
      secure_payload: memberSecurePayload,
      profile_image: member.profile_image,
    };

    // add to members in NewGroupChatState
    addChatMember(newMember);
  } catch (error) {
    // TO DO => Handle revert of optimistic updates in local DB

    // remove from usernames in NewGroupChatState => update check state
    removeUsername(username);
    // remove from members in NewGroupChatState

    removeChatMember(username);

    // Handle error
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Unable to add member.");
  }
};

export const prepareGroupChatOwner = async (): Promise<void> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  const csk = useNewGroupChatState.getState().newCSK;
  const chatId = useNewGroupChatState.getState().newChatId;
  const setChatUserAction = useNavigationState.getState().setChatUserAction;
  const resetNewGroupChatState =
    useNewGroupChatState.getState().resetNewGroupChatState;
  const setOwnerSecurePayload =
    useNewGroupChatState.getState().setOwnerSecurePayload;

  if (!csk) {
    throw new Error("CSK not provided.");
  }

  if (!chatId) {
    throw new Error("Chat ID not provided.");
  }

  try {
    const member = await getUserData(userFingerPrint.username);

    if (!member) {
      throw new Error("Auth not present");
    }

    // Get current user's private key
    const senderPrivateKeys = await decryptPrivateKey(
      userFingerPrint.ePrK,
      userFingerPrint.dk,
    );

    // Generate secure payload for owner
    const memberSecurePayload = encapsulateSecurePayload({
      receiverPublicKeys: userFingerPrint.ePuK,
      senderPrivateKeys,
      chatSecretKey: csk,
    });

    // add owner sp in mem = for immediate msg sending after chat create
    // used for encrypting msg content
    setOwnerSecurePayload(memberSecurePayload);

    // TO DO = > optimistically add to members collection in local db
    useNewGroupChatState.setState({
      members: [
        {
          username: userFingerPrint.username,
          chat_id: chatId,
          role: MemberRole.OWNER,
          secure_payload: memberSecurePayload,
          profile_image: member.profile_image,
        },
      ],
    });
  } catch (error) {
    // TO DO => Handle revert of optimistic updates in local DB

    //  additional cleanup of NewGroupChatState state and navigation state
    resetNewGroupChatState();
    setChatUserAction(null);
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot prepare chat owner.");
  }
};

export const removeMemberFromChat = async ({
  chatId,
  username,
}: {
  chatId: string;
  username: string;
}): Promise<void> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  if (!chatId) {
    throw new Error("Chat ID not provided.");
  }
  if (!username) {
    throw new Error("Username not provided.");
  }

  // const setActiveChatRemovedRoleAsMember =
  //   useNavigationState.getState().setActiveChatRemovedRoleAsMember;

  try {
    // TO DO =>  Check for possible optimistic updates
    // TO DO => chek if chat id is same as active chat id
    // and update the local db from here as well as from ws events

    await deleteMember({
      chat_id: chatId,
      username,
    });

    // If we removed another member (not self), rotate the CSK so
    // the removed member cannot decrypt future messages (spec §6).
    if (userFingerPrint.username !== username) {
      try {
        await rotateCSK(chatId, "member_removed");
      } catch (rotationError) {
        // Log but don't fail the removal — rotation can be retried
        console.warn(
          "CSK rotation after member removal failed:",
          rotationError,
        );
      }
    }

    // if current user is leaving chat
    if (userFingerPrint.username === username) {
      // await updateChatInLocalDB(chatId, {
      //   roleAsMember: {
      //     username,
      //     role: MemberRole.MEMBER, // TO DO => do we assume only members can leave?
      //     removed: true,
      //   },
      // });

      const activeChatId = useNavigationState.getState().activeChatId;

      // if chat id is same as active chat id, navigate away via URL (URL-first pattern)
      if (activeChatId === chatId) {
        navigateToChatList();
      }

      // delete messages + attachments (incl. OPFS blobs) + links from local
      // db, THEN the chat row (sweep-before-row, matching the other sites)
      await removeChatDataFromLocalDB(chatId);
      await removeChatFromLocalDB(chatId);
    }

    // TO DO => implement request cache invalidation -  REact Query
  } catch (error) {
    // TO DO => Handle revert of optimistic updates in local DB
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot remove member");
  }
};

export const addMembersToGroupChat = async (
  payload: GroupChatMember[],
): Promise<void> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    // TO DO =>  Check for possible optimistic updates
    // TO DO => chek if chat id is same as active chat id
    // and update the local db from here as well as from ws events

    await addMembers(payload);

    // TO DO => implement request cache invalidation -  REact Query
  } catch (error) {
    // TO DO => Handle revert of optimistic updates in local DB
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot add members");
  }
};

export type MemberByChatIdWithAuth = Member & {
  isAuth: boolean;
};

export const getGroupChatMembers = async (
  chatId: string,
  offset: number,
  count: number,
): Promise<MemberByChatIdWithAuth[]> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    const response = await getMembers({
      chat_id: chatId,
      skip: offset,
      take: count,
    });

    // Seed the avatar map with member avatars
    seedAvatarMapFromMembers(response);

    const processedResults = response.map((result) => ({
      ...result,
      isAuth: result.username === userFingerPrint.username,
    }));

    return processedResults;
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot fetch members");
  }
};

export type MemberFromPrefixSearchWithAuth = MemberFromPrefixSearch & {
  isAuth: boolean;
};

export const getMembersWithPrefixSearch = async (
  searchTerm: string,
  offset: number,
  count: number,
  member?: string, // pass chatId - gets members INSIDE that chat
  not_member?: string, // pass chatId - gets members OUTSIDE that chat
): Promise<MemberFromPrefixSearchWithAuth[]> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    const response = await searchMembers({
      searchTerm,
      member,
      not_member,
      offset,
      count,
    });

    // Seed the avatar map with member avatars
    seedAvatarMapFromMembers(response);

    const processedResults = response.map((result) => ({
      ...result,
      isAuth: result.username === userFingerPrint.username,
    }));

    return processedResults;
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot fetch members");
  }
};

export const updateMemberRole = async (
  chatId: string,
  username: string,
  role: MemberRole,
): Promise<void> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Chat ID not provided.");
  }
  if (!username) {
    throw new Error("Username not provided.");
  }

  if (!Object.values(MemberRole).includes(role)) {
    throw new Error("Role not provided.");
  }

  try {
    // TO DO =>  Check for possible optimistic updates

    const memberDetails = await getMember(username, chatId);

    await patchMember({
      username,
      chat_id: chatId,
      role,
      secure_payload: memberDetails.secure_payload,
      profile_image: memberDetails.profile_image,
    });
  } catch (error) {
    // TO DO => Handle revert of optimistic updates in local DB
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot update member");
  }
};

export const transferOwnership = async (
  username: string,
  chatId: string,
): Promise<void> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Chat ID not provided.");
  }
  if (!username) {
    throw new Error("Username not provided.");
  }

  try {
    // TO DO =>  Check for possible optimistic updates

    // const memberDetails = await getMember(username, chatId);

    await patchMemberOwnership({
      to: username,
      chat_id: chatId,
    });
  } catch (error) {
    // TO DO => Handle revert of optimistic updates in local DB
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot transfer ownership");
  }
};
